import type { ReactElement } from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import { applyUpdate, dismissUpdate, updateOffered } from '../../core/pwa/update.store';

/**
 * The update offer (021 FR-011, FR-014, FR-020, FR-029).
 *
 * It exists because installability creates the problem. A browser tab picks up a new version
 * whenever it is reloaded; an installed window is one nobody reloads, and the worker keeps serving
 * what it has. So the application has to say so itself.
 *
 * `role="status"` with `aria-live="polite"` (FR-020): it appears without the practitioner doing
 * anything, so it must be announced — but ASSERTIVELY would cut across whatever a screen reader is
 * reading, and moving focus to it would take the caret out of a half-typed action, which is the
 * harm FR-012 exists to prevent. It is announced at the next natural pause and takes nothing; the
 * button is reachable in normal tab order rather than by being forced under the cursor.
 *
 * Dismissal is "not now", never "never" (FR-029): the offer returns at the next check, which is
 * what keeps FR-014 true — dismissing can never leave a client unable to reach the current
 * version.
 *
 * 020's rule — a control that cannot cancel what it appears to cancel must not be offered — is
 * honoured by the WHOLE BANNER unmounting the moment the store starts applying, not by a button
 * going inert. `updateOffered` folds `applying` in, so there is no window in which these buttons
 * are on screen and unable to do what they say.
 *
 * It spends no colour (FR-033). A new version is not a time pressure, and the four urgency bands
 * are the only hues this application paints.
 */
export function UpdateBanner(): ReactElement | null {
  const offered = updateOffered.use();
  if (!offered) return null;

  return (
    <Box
      role="status"
      aria-live="polite"
      data-testid="update-banner"
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 'var(--space-3)',
        padding: 'var(--space-2) var(--space-4)',
        background: 'var(--paper-raised)',
        borderBottom: '1px solid var(--rule)',
        fontSize: 'var(--text-sm)',
        color: 'var(--ink)',
        '& > span': { flex: '1 1 auto' },
        '@media (prefers-reduced-motion: reduce)': { transition: 'none', animation: 'none' },
      }}
    >
      <span>A new version of CompleteMe is ready.</span>
      <Button data-testid="update-apply" onClick={() => void applyUpdate()}>Update</Button>
      <Button data-testid="update-dismiss" onClick={dismissUpdate}>Not now</Button>
    </Box>
  );
}
